/**
 * The ledger/report refusal classifier — the read half of the N3 seam.
 *
 * Every one of the sixteen N3 RPCs (0009-0017) refuses by RAISEing a message
 * that begins with a SENTINEL token and carries an explicit SQLSTATE. Those
 * refusals arrive in the browser as PostgrestError objects, and a user-facing
 * app has to tell them apart: "this entry doesn't balance" and "you may not
 * write this org's books" are the same HTTP failure and completely different
 * screens. Without this module every call site would hand-roll the same
 * substring matching, and they would not agree.
 *
 * This module imports one thing — src/data/accessGuard.js — because the
 * transport layer was already measured there and MUST NOT be re-derived here.
 * See "WHAT IS DELEGATED" below.
 *
 * ===========================================================================
 * THE RULE, AND WHY IT INVERTS accessGuard's
 * ===========================================================================
 *
 * accessGuard's rule is "SQLSTATE 42501 AND the not_a_member sentinel", and it
 * says both halves are load-bearing. Here the halves carry the opposite weights,
 * and copying that rule verbatim would have been wrong:
 *
 *   - The SENTINEL is unique. `ledger_delete_cleared:` is emitted by exactly one
 *     RAISE in exactly one function. Nothing else can produce it.
 *   - The SQLSTATE is SHARED, heavily. 23503 covers six different sentinels
 *     (entry not found, four delete refusals, the cleared starting balance, plus
 *     account/receipt not found); 23514 covers three; 23505 covers two; 42501
 *     covers three including one that belongs to accessGuard.
 *
 * So the sentinel decides and the SQLSTATE does not gate. Requiring the pair at
 * runtime would add no discrimination the sentinel does not already have, and
 * would add a way for this file to silently stop working: a migration that
 * changes one ERRCODE would drop that refusal into the generic bucket and the
 * user would get "something went wrong" instead of "unclear the reconciliation
 * first". The SQLSTATE is still recorded on every verdict, and the test suite
 * asserts each recorded value against the actual RAISE in supabase/migrations —
 * so drift is caught mechanically, at the place that can fix it, rather than
 * degrading a live screen.
 *
 * The sentinel is matched ANCHORED at the start and followed by a colon, exactly
 * as accessGuard does, so echoed user text (a memo, an account name) that merely
 * contains the phrase cannot forge a verdict.
 *
 * ===========================================================================
 * WHAT IS DELEGATED, AND WHY NOTHING HERE RE-DERIVES IT
 * ===========================================================================
 *
 * accessGuard's header carries a MEASURED table (2026-09-05, against the live
 * local stack) of how a token problem, an anonymous caller, deploy skew and a
 * dead socket actually present. Every failure that carries no N3 sentinel is
 * handed to `classifyAccessError` and its verdict is mapped, so:
 *
 *   - `not_a_member` keeps ONE implementation. This module never matches that
 *     sentinel itself; a session teardown decision must not have two authors.
 *   - a bare 42501 stays UNAUTHENTICATED, not "permission denied". That
 *     reasoning — an anonymous caller gets 42501 because `anon` holds no EXECUTE
 *     grant, so 42501 alone says nothing about permission — applies here
 *     verbatim, and a ledger writer whose token has not loaded yet must be told
 *     to wait, not told they lack the bookkeeper role.
 *   - PGRST301/302/303, PGRST202/203/204 and the empty-code network case need no
 *     second table.
 *
 * ===========================================================================
 * RETRYABLE MEANS ONE PRECISE THING
 * ===========================================================================
 *
 *     retryable = re-sending this EXACT request may succeed with nobody
 *                 changing anything first.
 *
 * It does NOT promise the previous attempt failed to commit. A dropped socket
 * can lose the response to a write that succeeded, which is precisely why the
 * mutation RPCs take `p_request_id` and enforce it (0009: same key + same
 * payload returns the original, same key + different payload is refused). A
 * retried MUTATION must therefore re-send the SAME `p_request_id`; retrying with
 * a fresh one double-posts. src/data/rpc.js deliberately does not retry for you.
 *
 * Two consequences that look wrong and are not:
 *
 *   T3_STALE_VERSION is NOT retryable. The request embeds
 *   `p_expected_version_id`; the server just said that is not the current
 *   version, and it will not become the current version again. An automatic
 *   retry loops forever. A human must reload and re-decide.
 *
 *   It used to carry SQLSTATE 40001 — the class whose entire convention is
 *   "retry me" — and that was not merely wrong as documentation, it HUNG THE
 *   BROWSER. PostgREST v14 (what Supabase runs) wraps every request in
 *   hasql-transaction's retrying variant, which retries UNBOUNDEDLY on 40001
 *   and 40P01; the retry was bounded upstream only in PostgREST 16 (PR #4222,
 *   issue #3673). A deterministic 40001 therefore spun BEGIN → RAISE → ROLLBACK
 *   forever and no HTTP response was ever written, so the edit dialog sat open
 *   with no message and a Save button that never re-enabled. RESOLVED by
 *   supabase/migrations/0271_n3_stale_version_answers.sql: the three edit doors
 *   (amend_entry, annotate_entry, delete_entry) now raise `PT409`, PostgREST's
 *   own "answer this as HTTP nnn" class, which is never retried. The rule going
 *   forward is in that file's header: an application-level conflict raises
 *   PT409, never class 40.
 *
 *   A 40001 still arrives here from doors other waves own (the bank-feed,
 *   transfer and card-payment pair doors, `job_lease_lost`, `import_batch_race`
 *   — 0271's header lists them file and line), and a 40001 WITHOUT a sentinel
 *   IS retryable: that is an ordinary Postgres serialization failure or
 *   deadlock, and re-sending is the documented remedy. Same SQLSTATE, opposite
 *   verdict, decided entirely by the sentinel.
 *
 * ===========================================================================
 * MESSAGES: WHAT IS APP COPY AND WHAT IS THE SERVER'S OWN WORDS
 * ===========================================================================
 *
 * Almost none of the server messages are finished UI copy, because almost all of
 * them interpolate opaque ids and raw cent integers:
 *
 *     ledger_delete_cleared: entry ent_9fK2 is cleared by reconciliation
 *     rec_71Qa — unclear it first, or post a reversal
 *
 * The valuable half of that is the remedy after the dash, which carries no ids.
 * So for the ledger refusals `message` is app copy that KEEPS the server's own
 * prescribed remedy verbatim and drops the ids, and the untouched server string
 * is always available as `detail` for a disclosure or a log line.
 *
 * The one family that IS passed through unmodified is the 22023 report-parameter
 * errors (`report_bad_grain`, `report_bad_period`, `report_bad_type`,
 * `report_dense_needs_accounts`, `report_missing_*`). They interpolate no ids,
 * they enumerate the legal values, and they are developer-facing — a user never
 * chooses a grain string, and never chooses an empty account list. Inventing
 * copy for them would only lose information. `passthrough: true` says which is
 * which, so nobody has to guess whether they are reading our words or Postgres's.
 *
 * ===========================================================================
 * FAULT
 * ===========================================================================
 *
 *   FAULT_USER    the person at the screen can resolve it — balance the entry,
 *                 unclear the reconciliation, reload, ask an admin for a role.
 *                 Show `message`. Do NOT page anyone.
 *   FAULT_SYSTEM  nothing the person does changes the outcome; it needs an
 *                 engineer, a deploy, or just time. Show `message`, and this is
 *                 the half worth reporting to Sentry.
 */
import {
  classifyAccessError,
  REVOKED,
  CONTROL_ENDED,
  UNAUTHENTICATED,
  UNSUPPORTED,
  TRANSIENT,
} from "./accessGuard.js";
import { captureDataFault, dataBreadcrumb } from "./telemetry.js";

/** Who can do something about it. See the header. */
export const FAULT_USER = "user";
export const FAULT_SYSTEM = "system";

/**
 * EVERY sentinel this app classifies, keyed by the token each function RAISEs.
 *
 * ONE TABLE, deliberately. It started as the N3 ledger doors and the report
 * read path; N7's job and import doors were merged in from two satellite tables
 * that used to be re-applied after the fact (see the N7 block below for what
 * that cost). A second table anywhere means the classifier answers twice, and
 * the first answer is the one Sentry already saw.
 *
 * `sqlstate` is documentation, not a gate — reportErrors.test.js asserts every
 * value here against the actual `RAISE EXCEPTION` in supabase/migrations, so
 * this table cannot drift from the database without a test failing.
 */
const SENTINELS = {
  // --- concurrency (0009, 0010, 0010's delete; 0271 moved it off class 40) --
  T3_STALE_VERSION: {
    sqlstate: "PT409",
    retryable: false,
    fault: FAULT_USER,
    message:
      "Someone else changed this entry while you had it open. Reload it and make your change again.",
  },

  // --- the caller gate (0009) -------------------------------------------
  not_ledger_writer: {
    sqlstate: "42501",
    retryable: false,
    fault: FAULT_USER,
    // Names the roles because the remedy is "ask someone to change your role",
    // and a bare "no permission" leaves the user with nowhere to go. The list
    // mirrors t3.assert_org_ledger_writer exactly.
    message:
      "You don't have permission to change this organization's books. Owners, admins and bookkeepers can post entries.",
  },
  fund_not_writable: {
    sqlstate: "42501",
    retryable: false,
    fault: FAULT_USER,
    message: "You don't have access to one of the funds used on this entry.",
  },

  // --- the idempotency door (0009) --------------------------------------
  ledger_request_id_conflict: {
    sqlstate: "23505",
    retryable: false,
    fault: FAULT_SYSTEM,
    // Not the user's fault: they cannot see or choose a request id. A reused
    // key with a different payload is a client defect, and retrying is futile —
    // the key stays used. The reassurance is load-bearing: the whole point of
    // the door is that nothing was written twice.
    message:
      "This change was sent with an id that had already been used for a different change, so it was refused. Nothing was saved twice.",
  },

  // --- lifecycle refusals (0010) ----------------------------------------
  ledger_already_reversed: {
    sqlstate: "23505",
    retryable: false,
    fault: FAULT_USER,
    message: "This entry has already been reversed.",
  },
  ledger_entry_not_found: {
    sqlstate: "23503",
    retryable: false,
    fault: FAULT_USER,
    message: "That entry is no longer in this organization — it may have been deleted.",
  },
  ledger_delete_cleared: {
    sqlstate: "23503",
    retryable: false,
    fault: FAULT_USER,
    message: "This entry is cleared by a reconciliation — unclear it first, or post a reversal.",
  },
  ledger_delete_reversed: {
    sqlstate: "23503",
    retryable: false,
    fault: FAULT_USER,
    message: "This entry has been reversed — delete the reversal first.",
  },
  ledger_delete_donation: {
    sqlstate: "23503",
    retryable: false,
    fault: FAULT_USER,
    message: "This entry records a donation — void the donation instead.",
  },
  ledger_delete_linked_transfer: {
    sqlstate: "23503",
    retryable: false,
    fault: FAULT_USER,
    message: "This entry is one leg of a linked transfer — delete the transfer instead.",
  },

  // --- balance and shape (0009) -----------------------------------------
  //
  // ORDER TRAP: `ledger_unbalanced` is a strict prefix of
  // `ledger_unbalanced_fund`. Both are exact keys and the token is extracted up
  // to the first colon, so neither can shadow the other — but a future
  // startsWith() refactor would silently collapse them, and the two need
  // different remedies: an entry can be perfectly balanced org-wide and still be
  // out fund by fund, which is the mistake fund accounting actually produces.
  ledger_unbalanced: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    message: "This entry doesn't balance — total debits must equal total credits.",
  },
  ledger_unbalanced_fund: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    message:
      "This entry doesn't balance within every fund. Debits and credits have to match fund by fund, not just overall.",
  },
  ledger_no_lines: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    message: "This entry has no lines. Add at least one debit and one credit.",
  },

  // --- starting balances (0011) -----------------------------------------
  starting_balance_cleared: {
    sqlstate: "23503",
    retryable: false,
    fault: FAULT_USER,
    message:
      "This opening balance is cleared by a reconciliation and can't be changed. Unclear it first.",
  },
  starting_balance_empty: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    message: "Enter a non-zero opening balance for at least one fund.",
  },
  account_not_found: {
    sqlstate: "23503",
    retryable: false,
    fault: FAULT_USER,
    message: "That account is no longer in this organization.",
  },

  // --- contacts (0030/0115/0130/0145/0148/0213/0225, and 0260) -----------
  //
  // The whole `contact_` family is now scanned across every migration by
  // reportErrors.test.js, for the reason `general_ledger_` was added to that
  // scan: a refusal a user CAN cause, arriving as "this change conflicts with
  // data already in the books", is the exact failure the drift gate exists for.
  // These are write doors rather than read primitives, but every one of them is
  // reachable from a picker the moment `create_contact` is wired, and the
  // remedy for each is something the person at the screen can do.
  contact_not_found: {
    sqlstate: "23503",
    retryable: false,
    fault: FAULT_USER,
    // The wording `account_not_found`'s already uses, for the same event: the
    // row was there when the screen loaded and is not there now.
    message: "That contact is no longer in this organization — it may have been deleted.",
  },
  contact_not_mergeable: {
    sqlstate: "23503",
    retryable: false,
    fault: FAULT_USER,
    // Distinct from `contact_not_found` on purpose: the row IS there, it is
    // just not a LIVE contact ("disabled, or already merged away"), and the
    // remedy is to pick a different one rather than to reload. Collapsing the
    // two into one message would send the user to the wrong screen.
    message:
      "One of the contacts you chose can't be merged — it's disabled or has already been merged away.",
  },
  contact_short_name_blank: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    // `create_contact` takes no first/last name, so a Person with no short name
    // cannot be derived and is refused. The copy has to name the two things the
    // caller CAN pass, because "a contact needs a name" is not actionable when
    // the field they typed into was the first-name box.
    message: "This contact needs a name — enter a name, or a company name for a vendor.",
  },
  contact_field_too_long: {
    sqlstate: "22001",
    retryable: false,
    fault: FAULT_USER,
    // PASSTHROUGH, unlike every other contact refusal here. The server's
    // sentence names the field and its limit ("company name is 46 characters —
    // the limit is 45") and interpolates no ids; app copy could only be vaguer.
    // Same call as `report_bad_grain`, made for the opposite kind of message.
    passthrough: true,
  },
  contact_email_invalid: {
    sqlstate: "22023",
    retryable: false,
    fault: FAULT_USER,
    // NOT passthrough: the server echoes the address the user typed, and
    // reflecting user input back into app copy is how an error banner becomes a
    // rendering question. The address is one field away on the screen.
    message: "That email address doesn't look right — check it and try again.",
  },
  contact_name_exists: {
    sqlstate: "23505",
    retryable: false,
    fault: FAULT_USER,
    // The server's sentence carries the existing contact's id, which is exactly
    // the header's case for app copy: the id is worthless to a reader and the
    // remedy after the dash is the valuable half. A caller that wants to SELECT
    // the existing contact reads it out of `detail`, which is untouched.
    message: "A contact with that name already exists. Use it, or choose a different name.",
  },

  // --- donors (0043's merge door, and 0261's create door) ----------------
  //
  // The `donor_` family joined the cross-migration scan with 0261 for the reason
  // `contact_` joined it with 0260: a write door in front of a PICKER, whose
  // refusals are all things a person at a keyboard actually does. The prefix
  // also swept in the four `merge_donors` has raised since 0043 and nothing has
  // ever classified — the donor merge screen has been showing "this change
  // conflicts with data already in the books" for every one of them.
  //
  // The five create refusals are `contact_`'s, plus one contacts do not have.
  donor_short_name_blank: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    // Names the three things the caller CAN pass, because 0261 derives from any
    // of them and "a donor needs a name" is not actionable when the field they
    // typed into was the first-name box.
    message:
      "This donor needs a name — enter a name, a company name, or a first and last name.",
  },
  donor_field_too_long: {
    sqlstate: "22001",
    retryable: false,
    fault: FAULT_USER,
    // PASSTHROUGH, for `contact_field_too_long`'s reason: the server's sentence
    // names the field and its limit ("company name is 46 characters — the limit
    // is 45") and interpolates no ids, so app copy could only be vaguer.
    passthrough: true,
  },
  donor_email_invalid: {
    sqlstate: "22023",
    retryable: false,
    fault: FAULT_USER,
    // NOT passthrough: the server echoes the address the user typed, and
    // reflecting user input back into app copy is how an error banner becomes a
    // rendering question.
    message: "That email address doesn't look right — check it and try again.",
  },
  donor_name_exists: {
    sqlstate: "23505",
    retryable: false,
    fault: FAULT_USER,
    // The server's sentence carries the existing donor's id; a caller that wants
    // to SELECT that donor reads it out of `detail`, which is untouched.
    message: "A donor with that name already exists. Use it, or choose a different name.",
  },
  donor_email_exists: {
    sqlstate: "23505",
    retryable: false,
    fault: FAULT_USER,
    // The one refusal contacts have no counterpart for, and the only one in this
    // family backed by a real unique index (`donors_org_email_uq`, 0001:6685) —
    // so unlike `donor_name_exists` it is an invariant rather than a courtesy,
    // and the remedy has to be a remedy rather than "try again".
    message:
      "Another donor already uses that email address. Use that donor, or leave this one's email blank.",
  },
  // --- the merge door (0043), swept in by the prefix ----------------------
  donor_not_found: {
    sqlstate: "23503",
    retryable: false,
    fault: FAULT_USER,
    // `contact_not_found`'s wording for the same event: the row was there when
    // the screen loaded and is not there now.
    message: "That donor is no longer in this organization — it may have been deleted.",
  },
  donor_merge_into_self: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    message: "Choose two different donors — a donor can't be merged into itself.",
  },
  donor_survivor_is_merged: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    // Distinct from `donor_already_merged` on purpose: this one is about the
    // donor the user chose to KEEP, and the remedy is to pick the survivor of
    // the earlier merge. Collapsing the two would send them to the wrong field.
    message:
      "The donor you chose to keep was itself merged away — merge into the surviving donor instead.",
  },
  donor_already_merged: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    message: "One of the donors you chose has already been merged away.",
  },

  // --- receipts (0010) ---------------------------------------------------
  receipt_not_found: {
    sqlstate: "23503",
    retryable: false,
    fault: FAULT_USER,
    message: "That receipt is no longer attached to this entry.",
  },

  // --- reconciliation (0031, and its two respecifications 0150/0151) ------
  //
  // WHY THESE ARE HERE AT ALL. Every one of them is SQLSTATE class 23, and
  // class 23 with no sentinel entry is `constraint_violation`: FAULT_SYSTEM,
  // not retryable, which `reportIfSystemFault` CAPTURES. So before this block
  // a reconciler who pressed Complete one transaction short saw "This change
  // conflicts with data already in the books, so nothing was changed" — and
  // minted a Sentry issue for it. Twenty-one refusals, every one of them an
  // ordinary screen, all arriving as incidents. That is the exact failure
  // `report_dense_needs_accounts` and `ledger_entries_page` were added for,
  // one subsystem later, and the drift gate is widened with them
  // (reportErrors.test.js's family regex) so 0031's successor cannot repeat it.
  //
  // FAULT IS USER FOR ALL BUT TWO. The person at the reconciliation screen can
  // resolve every one of these: pick a date, clear one more transaction,
  // un-tick a row dated past the closing date, reopen the later rec first. The
  // two exceptions are named at their entries.
  //
  // COPY KEEPS THE SERVER'S REMEDY AND DROPS THE IDS/CENTS, per MESSAGES above
  // — these sentences interpolate reconciliation ids, account ids and raw cent
  // integers ("cleared to 1234500 against ... clear 45 more cents"), so none of
  // them is passthrough. The untouched server string stays on `detail`.
  reconciliation_not_found: {
    sqlstate: "23503",
    retryable: false,
    fault: FAULT_USER,
    message: "That reconciliation is no longer in this organization — it may have been deleted.",
  },
  reconciliation_id_taken: {
    sqlstate: "23505",
    retryable: false,
    fault: FAULT_SYSTEM,
    // Not the user's fault, for `ledger_request_id_conflict`'s reason exactly:
    // the reconciliation id is minted by the client (`mintRecId`), never seen
    // or chosen by a person, and a collision across orgs is a defect in that
    // minting. Not retryable — the id stays taken; a fresh attempt mints a new
    // one, which is a DIFFERENT request.
    message:
      "This reconciliation couldn't be started because its id was already in use. Nothing was changed — try starting it again.",
  },
  rec_closing_date_required: {
    sqlstate: "23502",
    retryable: false,
    fault: FAULT_USER,
    message: "Select a closing date for this reconciliation.",
  },
  rec_closing_balance_required: {
    sqlstate: "23502",
    retryable: false,
    fault: FAULT_USER,
    // Zero is a legitimate closing balance (GH#222); absent is not.
    message: "Enter a closing balance for this reconciliation.",
  },
  rec_already_open: {
    sqlstate: "23505",
    retryable: false,
    fault: FAULT_USER,
    // Raised by start (continue the open one) AND by reopen/discard (finish the
    // open one first). One sentence has to serve both doors.
    message:
      "This account already has a reconciliation open — continue, finish or discard it before starting another.",
  },
  rec_period_not_ended: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    message: "The last reconciliation on this account hasn't closed yet — the next one can start tomorrow.",
  },
  rec_closing_date_not_after_last: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    message: "The closing date has to be after the last completed reconciliation on this account.",
  },
  rec_complete: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    message: "This reconciliation is complete — reopen it before changing what it clears.",
  },
  rec_not_newest: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    message: "A later reconciliation on this account is still complete — reopen that one first.",
  },
  rec_has_clearings: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_SYSTEM,
    // The second exception. 0151 calls this branch unreachable while completion
    // and reopen remain the only writers of `entry_clearings`, and says so
    // because the FK is ON DELETE CASCADE: reaching it means a period would
    // silently stop being reconciled. An invariant breach, not a screen.
    message:
      "This reconciliation isn't complete but still claims cleared transactions, so it wasn't deleted. Reopen it first.",
  },

  // The tick door (`mark_entry`), which names the offending row one at a time
  // so the caller can revert exactly that tick — `parseRefusal`
  // (src/data/reconciliationMarks.js) lifts the entry id back out of `detail`.
  // `ledger_entry_not_found` is raised here too and is already classified above.
  rec_entry_not_on_account: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    message: "That transaction has no line on this account, so it can't be cleared here.",
  },
  rec_no_cash_movement: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    message:
      "That transaction nets to zero on this account — there's nothing on the statement to clear it against.",
  },
  rec_out_of_period: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    // Raised on the tick ("it belongs to the next reconciliation") and again at
    // completion, where an entry re-dated after it was ticked is caught ("un-
    // clear or re-date it"). Both remedies, since one token serves both.
    message:
      "That transaction is dated after the closing date — un-clear it or re-date it. It belongs to the next reconciliation.",
  },
  rec_already_cleared: {
    sqlstate: "23505",
    retryable: false,
    fault: FAULT_USER,
    message: "That transaction is already cleared on this account by another reconciliation.",
  },

  // Completion's own four gates.
  rec_unvalidatable: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    message:
      "Only asset and liability accounts have a statement balance to check a reconciliation against.",
  },
  rec_opening_balance_mismatch: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    message:
      "This reconciliation's opening balance doesn't match what the previous one closed at. Fix the figure, or override it with a reason.",
  },
  rec_override_needs_reason: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    message: "An opening-balance override has to say why.",
  },
  rec_out_of_balance: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    // The reconciliation error. The exact difference stays on `detail` in
    // cents; the screen already shows it in dollars.
    message:
      "What you've cleared doesn't add up to the statement's closing balance. Clear or un-clear transactions until the difference is zero.",
  },

  // Statement promotion (0031/0150), the auto-reconciliation hand-off.
  statement_not_found: {
    sqlstate: "23503",
    retryable: false,
    fault: FAULT_USER,
    message: "That bank statement is no longer in this organization.",
  },
  statement_wrong_account: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    message: "That statement was uploaded against a different account.",
  },
  statement_already_promoted: {
    sqlstate: "23505",
    retryable: false,
    fault: FAULT_USER,
    message: "That statement has already been turned into a reconciliation.",
  },
  statement_path_taken: {
    sqlstate: "23505",
    retryable: false,
    fault: FAULT_SYSTEM,
    // 0152. `bank_statements.storage_path` is globally unique across tenants,
    // and the path is MINTED BY THE APP on upload (nobody types one), so a
    // collision is the app's path scheme, not the person's file. Not retryable:
    // the path stays taken until a different one is minted, which is a
    // different request. Found by the family drift gate rather than by anyone
    // remembering 0152 exists, which is the gate working.
    message:
      "This statement couldn't be registered because its storage location was already in use. Nothing was changed — try uploading it again.",
  },

  // --- report parameters (0013, 0014, 0017) ------------------------------
  // passthrough: no ids, enumerates the legal values, developer-facing. See the
  // header. fault is SYSTEM because a user never types a grain — a bad one means
  // the app built the request wrong.
  report_bad_grain: { sqlstate: "22023", retryable: false, fault: FAULT_SYSTEM, passthrough: true },
  report_bad_period: { sqlstate: "22023", retryable: false, fault: FAULT_SYSTEM, passthrough: true },
  report_bad_type: { sqlstate: "22023", retryable: false, fault: FAULT_SYSTEM, passthrough: true },
  // 0141/0242/0244/0245, not 0013-0017: a dense report over an EMPTY account
  // list is refused because it has no rows to be dense about. Passthrough for
  // the same reason as the three above — it names a parameter, not a user's
  // choice, so the server's sentence ("pass NULL to frame on the org's chart,
  // or pass the accounts you meant") is the remedy and no app copy improves on
  // it. Without an entry here it fell through to SQLSTATE 22023's generic
  // "the app sent a request the server couldn't process".
  report_dense_needs_accounts: {
    sqlstate: "22023",
    retryable: false,
    fault: FAULT_SYSTEM,
    passthrough: true,
  },
  // 0251: `general_ledger_page` refuses a `p_filter_logic` that is not AND/OR.
  // The shaper normalises the screen's value before the call (request.js
  // `logicOrDefault`), so reaching this is a shaper bug, not the user's — the
  // same class as `report_dense_needs_accounts`.
  report_filter_logic_invalid: {
    sqlstate: "22023",
    retryable: false,
    fault: FAULT_SYSTEM,
    passthrough: true,
  },
  // The GL page's OTHER two refusals, which do not spell themselves
  // `report_*`: 0251:273 `general_ledger_page: p_limit must be a positive
  // integer` and 0251:282 `general_ledger_page: partial cursor …`. Both
  // tokenise to the FUNCTION's name — `SENTINEL_TOKEN` takes everything before
  // the first colon — so one entry classifies both, and one entry is all there
  // can be: they share a token by construction.
  //
  // Neither is reachable from a screen. `PAGE_SIZE` is a module constant and
  // the cursor is built by `plan`'s own `cursorFrom` from the row the walk just
  // read, so a partial one means the runner lost a component between pages.
  // SYSTEM and passthrough for the same reason as the block above: the server's
  // sentence names the parameter and the shape that arrived ("got 2 of 4:
  // account=t, date=t, entry=f, line=f"), which is the whole diagnosis, and no
  // app copy improves on it. Without this the two loudest refusals in the
  // ledger's own primitive arrived at a reader as SQLSTATE 22023's generic "the
  // app sent a request the server couldn't process".
  general_ledger_page: {
    sqlstate: "22023",
    retryable: false,
    fault: FAULT_SYSTEM,
    passthrough: true,
  },
  // 0257 — the register / Journal Entries list primitive. EIGHT refusals
  // (`bad_limit`, `partial_cursor`, `cleared_needs_account`, `bad_cleared`,
  // `bad_filter_logic`, `balance_needs_account`, `ids_with_cursor`,
  // `too_many_ids`; contract §A), every one `ERRCODE '22023'` and every one
  // spelled `ledger_entries_page: <token>: <detail>`. `SENTINEL_TOKEN` takes
  // everything before the FIRST colon, so — exactly as with
  // `general_ledger_page` above — one entry classifies all eight, and one entry
  // is all there can be: they share a token by construction. The `<token>` half
  // is not lost, it is INDEXED separately (see REFUSAL_TOKEN below).
  //
  // NOT ONE OF THEM IS REACHABLE FROM A SCREEN. `p_limit` is clamped by
  // `toRpcArgs` to [1, MAX_PAGE]; the cursor is all-or-nothing because
  // `toRpcArgs` writes its three parts together from one `rowKey`; `p_cleared`
  // and `p_filter_logic` are validated by `normalizeFilters`; `p_cleared` and
  // `p_with_balance` are both gated on `filters.accountId`; by-id mode returns
  // before any cursor part is written; and `refresh` re-pages the window above
  // REPAGE_THRESHOLD (50), an order of magnitude under the 500-id cap. So every
  // one of the eight is the SHAPER's bug — the same class as
  // `report_filter_logic_invalid` (0251) — never the user's, and the person at
  // the screen can do nothing about any of them. SYSTEM, therefore, and
  // passthrough: the server's sentence names the parameter AND the value that
  // arrived ("got 2 of 3: date=t, created_at=t, entry_id=f"), which is the whole
  // diagnosis. Without this entry all eight arrived as SQLSTATE 22023's generic
  // "the app sent a request the server couldn't process" — still a system fault,
  // still reported, but stripped of the one sentence that says which of eight.
  ledger_entries_page: {
    sqlstate: "22023",
    retryable: false,
    fault: FAULT_SYSTEM,
    passthrough: true,
  },
  // 0248, and the one budget_* sentinel on the READ path — every other one is a
  // budget WRITE door behind the budget screen (0064/0194). `budget_to_actual`
  // and `yoy_budget_execution` refuse a `p_budget_ids` naming two published
  // versions of the same fiscal year rather than summing them: their months
  // overlap, so the sum doubles the year and produces a number that still looks
  // like a budget. SYSTEM and passthrough for the same reason as the four above
  // — no user types a budget id, so a colliding list means the app built the
  // request wrong, and the server's sentence ("pass one budget per fiscal year,
  // or NULL to let budgets_for_range pick the latest published version of
  // each") IS the remedy.
  budget_year_collision: {
    sqlstate: "22023",
    retryable: false,
    fault: FAULT_SYSTEM,
    passthrough: true,
  },

  // =========================================================================
  // N7 — THE JOB PROTOCOL (0044, 0159, 0160, 0161) AND THE IMPORT DOORS
  // (0045, 0158, 0262, 0264)
  // =========================================================================
  // These arrived in N7 W1 as two tables beside their callers — `JOB_SENTINELS`
  // in src/data/jobs.js, `IMPORT_SENTINELS` in src/data/imports.js — applied by
  // an `overlaySentinel()` helper AFTER `createRpc.rpc` had already classified
  // and already reported. That cost was real and is the reason they are here
  // now: every 23xxx token below was read by the base classifier as
  // `constraint_violation`, which is FAULT_SYSTEM and non-retryable, so ONE
  // SENTRY EVENT was captured for what these rows call an ordinary user
  // refusal. The overlay could fix the verdict a person saw; it could not
  // un-send that event. Merged, `classifyRpcError` answers once and `rpc()`
  // reports once.
  //
  // Nothing else changed in the move: same tokens, same faults, same copy. The
  // drift gate in reportErrors.test.js scans `job_`/`import_` across EVERY
  // migration now, so a new refusal in either family fails a test rather than
  // reaching a user as "this change conflicts with data already in the books".

  // --- enqueue (0044:87 -> 0161:50) ---------------------------------------
  job_kind_unknown: {
    sqlstate: "23503",
    retryable: false,
    fault: FAULT_SYSTEM,
    // The user cannot choose a kind; the app names it. An unknown one is a
    // deploy skew or a typo in a call site, and retrying cannot register it.
    message: "The app asked for a kind of background job this server doesn't know about yet.",
  },
  job_dedupe_key_conflict: {
    sqlstate: "23505",
    retryable: false,
    fault: FAULT_SYSTEM,
    // Same shape as `ledger_request_id_conflict`: the key is the app's, not the
    // person's, and a reused key with a different payload is a client defect.
    message:
      "This job was sent with an id that had already been used for a different job, so it was refused. Nothing was started twice.",
  },
  job_singleton_active: {
    sqlstate: "23505",
    retryable: false,
    fault: FAULT_USER,
    // A person CAN act on this one: wait, or cancel the running job.
    message:
      "This organization already has a job of this kind running. Wait for it to finish, or cancel it first.",
  },
  job_enqueue_conflict: {
    sqlstate: "23505",
    retryable: false,
    fault: FAULT_USER,
    message: "Another job just claimed this work. Refresh to see it.",
  },

  // --- the worker protocol (0160, 0170, 0224) ------------------------------
  job_not_found: {
    sqlstate: "23503",
    retryable: false,
    fault: FAULT_USER,
    message: "That job is no longer in this organization — it may have been cleaned up.",
  },
  job_lease_lost: {
    sqlstate: "40001",
    retryable: false,
    fault: FAULT_SYSTEM,
    // NOT retryable, despite the 40001, and the ONE place in this file where
    // the serialization class does not mean "try again": the lease is gone
    // because another worker — or this worker's own next attempt — took the
    // job, and repeating the call can only overwrite a run still in flight.
    // 0160's whole point. `SERIALIZATION_CODES` never sees it, because a
    // matched sentinel is returned before that rule is consulted.
    message: "This job was taken over by another run, so this one stopped without changing it.",
  },
  job_cancelled: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    // The remedy is in the sentinel's own text: discard the result, call
    // fail_job. That is a worker instruction, so the user copy is the outcome.
    message: "This job was cancelled while it was running, so its result was discarded.",
  },
  job_already_finished: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    message: "This job has already finished, so there is nothing to cancel.",
  },

  // --- the operator's doors (0268) -----------------------------------------
  job_admin_only: {
    sqlstate: "42501",
    retryable: false,
    fault: FAULT_SYSTEM,
    // A DELIBERATE re-raise of `t3.assert_platform_admin`'s `not_platform_admin`
    // by both 0268 doors, and the reason is this table: `not_platform_admin`
    // belongs to no sentinel family, so the classifier reads it as a bare 42501
    // and the operator gets a generic sentence. `job_admin_only` is in the
    // `job_` family the drift gate scans across every migration, so it is
    // classified and stays classified.
    //
    // SYSTEM rather than USER even though a person triggered it: BackendJobsPanel
    // mounts only from admin screens, so a browser reaching this means the
    // panel got wired somewhere it does not belong — the same reading
    // `not_service_role` gets, for the same reason.
    message: "Background jobs across organizations are visible to T3 platform admins only.",
  },

  // --- retention (0268, scheduled; no browser can reach these) --------------
  job_purge_window_invalid: {
    sqlstate: "22023",
    retryable: false,
    fault: FAULT_SYSTEM,
    // `import_purge_window_invalid`'s shape and its caller: `t3-job-prune`,
    // which only `postgres` can run. Classified anyway because the drift gate
    // requires it, and because an unclassified sentinel is how 0262's pair sat
    // unreachable-but-unreadable for a migration.
    message: "The app asked for an impossible job retention window. Nothing was changed.",
  },
  job_purge_limit_invalid: {
    sqlstate: "22023",
    retryable: false,
    fault: FAULT_SYSTEM,
    message: "The app asked for an impossible job retention batch size. Nothing was changed.",
  },

  // --- the machine caller gate (0001:4706, restated by 0170) ---------------
  not_service_role: {
    sqlstate: "42501",
    retryable: false,
    fault: FAULT_SYSTEM,
    // A browser reaching this means a machine door got wired to a user path —
    // the D4 boundary broken. Never the user's fault, never retryable.
    message: "That action belongs to the server, not to a signed-in session.",
  },

  // --- opening a batch (0045:72) ------------------------------------------
  import_file_hash_required: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_SYSTEM,
    // The user picked a file; the app failed to hash it. Nothing they can do.
    message: "The app couldn't identify this file, so the import was refused. Nothing was changed.",
  },
  import_batch_race: {
    sqlstate: "40001",
    retryable: true,
    fault: FAULT_SYSTEM,
    // The one genuinely retryable sentinel in the N7 set, and the sentinel text
    // says so itself: "retry opening this batch".
    message: "Two uploads of this file collided. Try again.",
  },
  import_batch_not_found: {
    sqlstate: "23503",
    retryable: false,
    fault: FAULT_USER,
    message: "That import is no longer in this organization — it may have been discarded.",
  },

  // --- staging (0158:79) ---------------------------------------------------
  import_batch_closed_to_staging: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    // Names the remedy the sentinel names: a new batch, not a retry.
    message:
      "This import has already started posting, so no more rows can be added to it. Upload the remaining rows as a new import.",
  },
  import_kind_unsupported: {
    sqlstate: "0A000",
    retryable: false,
    fault: FAULT_SYSTEM,
    // D9: eight of ten `import_kind` values have no implementation. A user who
    // reaches this was offered a flow that does not exist yet, which is a wiring
    // defect, not a mistake they made. Without this entry the base classifier
    // reads 0A000 as an unknown transport failure and tells the user to check
    // their connection.
    message: "This kind of import isn't available yet.",
  },

  // --- planning and posting (0158:203, :297) -------------------------------
  import_chunk_size_invalid: {
    sqlstate: "22023",
    retryable: false,
    fault: FAULT_SYSTEM,
    message: "The app asked for an impossible import batch size. Nothing was changed.",
  },
  import_chunk_not_found: {
    sqlstate: "23503",
    retryable: false,
    fault: FAULT_SYSTEM,
    // Only a worker names a chunk number, so only a worker can get this wrong.
    message: "Part of this import is missing. It was stopped rather than posted in part.",
  },
  import_batch_discarded: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    // 0158 fix #3. A person threw the file away while a worker was mid-flight;
    // the refusal is the feature.
    message: "This import was discarded, so the rest of it was not posted.",
  },
  // 0264's `close_import_batch` — three refusals, and they split by fault
  // rather than by which function raised them.
  //
  // The door is gated `assert_org_ledger_writer` and GRANTed to `authenticated`
  // (the same ACL as `post_import_chunk`), so BOTH a person on the import screen
  // and the host reach these. That is why two of the three are FAULT_USER: the
  // screen is where they are resolved. The host reads a refusal here as a fault
  // of its own regardless (importPost.js's `finish` step) because it calls this
  // only after its own loop said every chunk was committed — for the WORKER, any
  // of the three is an invariant break.
  import_batch_chunks_open: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    // The server names the count ("N of M pending"); the remedy is the same
    // whichever N it is, so the copy names the remedy and drops the numbers.
    message:
      "This import still has chunks waiting to be posted, so it wasn't closed. Post or reject the remaining rows first.",
  },
  import_batch_rows_unposted: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_SYSTEM,
    // The OTHER leg, and the one no person can cause: every chunk committed and
    // rows are still unposted, which means rows were staged outside the chunk
    // ledger — orphaned by a planning path, not by anything anyone clicked.
    message:
      "Some rows in this import were never included in it. It was left open rather than closed over them.",
  },
  import_batch_not_posting: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    message: "This import isn't posting, so there's nothing to close.",
  },

  // --- rejecting a row and discarding a batch (0045:739, 0158:481) ---------
  import_row_not_found: {
    sqlstate: "23503",
    retryable: false,
    fault: FAULT_USER,
    message: "That row is no longer part of this import.",
  },
  import_row_already_posted: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    message:
      "This row is already in the books — delete or reverse the entry instead of rejecting the row.",
  },
  import_reject_needs_actor: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_SYSTEM,
    // `reject_import_row` records WHO rejected. A service-role caller has no
    // uid, so this fires exactly when the host tries to use a user's door.
    message: "Rejecting a row records who did it, and this request had no signed-in user.",
  },
  import_batch_has_posted_rows: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    message:
      "Part of this import is already in the books, so it can't be discarded. Reverse those entries first.",
  },

  // --- matching a staged row, and rejecting a set of them (0266) -----------
  // `set_import_row_match` is the review screen's write door — the four
  // Autocompletes, the batch-match rows, copy-contacts-within-group — and
  // `reject_import_rows` is its "Exclude group" sibling. Both are browser doors
  // with no host caller at all, so every refusal below is raised at somebody's
  // click. 0266 also reuses `import_batch_not_found`, `import_kind_unsupported`,
  // `import_row_not_found`, `import_row_already_posted` and
  // `import_reject_needs_actor` above rather than restating them.
  import_batch_closed_to_matching: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    // NOT `import_batch_closed_to_staging`'s copy, which is why it is not that
    // token. That one's remedy is "upload the remaining rows as a new import",
    // and that is the wrong sentence for somebody who mis-clicked an account:
    // they do not want a second file, they want to know the correction now
    // belongs to the entry.
    message:
      "This import has already been posted, so its matches can't be changed. Edit the entries in the register instead.",
  },
  import_match_target_invalid: {
    sqlstate: "23503",
    retryable: false,
    fault: FAULT_USER,
    // The remedy is real: an Autocomplete rendered from a chart that has since
    // changed — an account disabled in another tab, a fund merged — offers an
    // option the org no longer has.
    message:
      "That account, fund, contact or label isn't in this organization any more. Reload the page and pick again.",
  },
  import_match_field_invalid: {
    sqlstate: "22023",
    retryable: false,
    fault: FAULT_SYSTEM,
    // No user types a column name. The screen passes one of four literals, so a
    // fifth is a wiring defect.
    message: "The app tried to match a column that doesn't exist. Nothing was changed.",
  },
  import_match_no_rows: {
    sqlstate: "22023",
    retryable: false,
    fault: FAULT_SYSTEM,
    // The selection is the screen's to build. An empty one reaching the door
    // means a button was enabled with nothing selected.
    message: "No rows were selected, so nothing was changed.",
  },
  import_match_confidence_invalid: {
    sqlstate: "22023",
    retryable: false,
    fault: FAULT_SYSTEM,
    // 95 where 0.95 belongs, which the numeric(4,3) column would otherwise
    // report as `numeric field overflow`, naming no column.
    message: "The app sent an out-of-range match confidence. Nothing was changed.",
  },
  import_batch_job_active: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_SYSTEM,
    // 0266's re-plan fix. `plan_import_chunks` now re-stamps `job_id` on its
    // already-planned early return so a retry after a FAILED job does not leave
    // the host reading a dead job — but only when the incumbent job is
    // terminal. A live one means two posters were minted for one batch, which
    // is a host defect and never something the person at the screen did.
    message:
      "This import is already being posted by another job. Wait for it to finish rather than starting a second one.",
  },

  // --- the donations import: a fence and a skip door (0267) ---------------
  // Both arrive with W3, and they split by fault the way this table always
  // splits: the person at the review screen can resolve the first and cannot
  // cause the second. 0267 also reuses `import_batch_not_found`,
  // `import_kind_unsupported`, `import_row_not_found`,
  // `import_row_already_posted`, `import_match_*` and
  // `import_batch_closed_to_matching` above rather than restating them — the
  // donations arm of `set_import_row_match` raises exactly 0266's set.
  import_batch_not_ready: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    // The fence `plan_import_chunks` grew for donations. Every one of the three
    // things it counts is something a person matches or excludes at the review
    // screen, and the server names the counts — but the remedy is the same
    // whichever count is non-zero, so the copy names the remedy. Without this
    // entry the refusal reads as "this change conflicts with data already in
    // the books" for a batch that simply is not finished.
    message:
      "Some rows still need a donor, a fund or an income account. Match them, or exclude them from this import.",
  },
  import_skip_needs_actor: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_SYSTEM,
    // `import_reject_needs_actor`'s sibling and not its synonym: skipping a row
    // is the only exclusion no CHECK audits, so `updated_by` is the whole
    // record of who decided. It fires exactly when a machine caller reaches a
    // user's door, which is the D4 boundary broken and never anybody's mistake
    // at a keyboard.
    message: "Skipping a row records who did it, and this request had no signed-in user.",
  },

  // --- retention (0262) ----------------------------------------------------
  // `purge_import_raw`'s window is a constant in the scheduled job, never a
  // number a person types, so an invalid one is the app's mistake. Caught here
  // only because the drift gate below now scans `import_` across every
  // migration; before the widening it fell to 22023's generic sentence.
  import_purge_window_invalid: {
    sqlstate: "22023",
    retryable: false,
    fault: FAULT_SYSTEM,
    message: "The app asked for an impossible retention window. Nothing was changed.",
  },
  import_purge_limit_invalid: {
    sqlstate: "22023",
    retryable: false,
    fault: FAULT_SYSTEM,
    // Same shape, same caller: the batch size the scheduled purge walks in. It
    // arrived in the same migration as the window and was caught the same way —
    // by the widened gate, not by anyone remembering there were two.
    message: "The app asked for an impossible retention batch size. Nothing was changed.",
  },

  // --- the budget EDITOR's write doors (0064, 0194, and 0259's settings) --
  //
  // Eighteen refusals behind one screen. Until now every one of them fell into
  // SQLSTATE 23514's generic "this change conflicts with data already in the
  // books" — including `budget_not_draft`, whose whole content is the remedy
  // ("copy it to a new draft to change it"), and the two over-allocation
  // refusals, which enumerate the exact account, month and cent amounts that
  // disagree. A budget is an annual artefact somebody presents to a board;
  // "something went wrong" on the publish button is the worst place in the app
  // to lose a server's sentence.
  //
  // FAULT here splits on a real line rather than on how the error looks. The
  // editor's grid is built from the org's own chart, so a dimension the server
  // cannot find is USER-fault (someone deleted the account) while a dimension
  // of the wrong KIND is SYSTEM-fault (the app sent an id that was never a
  // fund). Everything the app itself composes — the cell array's shape, its
  // dates, its uniqueness — is SYSTEM, because no choice the user makes on the
  // screen produces or fixes it.
  //
  // ⚠️ `src/data/budgets.js#toRpcCell` refuses three of these BEFORE the call
  // (a label with no fund, a mid-month date, a non-numeric amount) and names
  // the offending cell while doing it, which the server cannot: it refuses the
  // whole batch, and a pasted column is a hundred cells. The entries here are
  // the second line, for the batch that reaches the door another way.
  budget_not_found: {
    sqlstate: "23503",
    retryable: false,
    fault: FAULT_USER,
    message: "That budget is no longer in this organization — it may have been deleted.",
  },
  budget_draft_exists: {
    sqlstate: "23505",
    retryable: false,
    fault: FAULT_USER,
    // `one_draft_per_year` (0001:13580) is what makes this reachable, and it is
    // also what DELETED the blind-overwrite hazard the Firestore path had: two
    // `setDoc(draftRef, …)` calls with no merge and no existence check. The
    // remedy is a real choice the user has, so it is named.
    message:
      "There's already a draft budget for that year. Open the existing draft, or delete it first.",
  },
  budget_source_not_found: {
    sqlstate: "23503",
    retryable: false,
    fault: FAULT_USER,
    message: "The budget you're copying from is no longer in this organization.",
  },
  budget_fiscal_year_invalid: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    message: "Choose a fiscal year between 1900 and 2999.",
  },
  budget_label_needs_fund_mode: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_SYSTEM,
    // The screen hides the label toggle entirely when the fund mode is 'none'
    // (CreateEditBudget.jsx:1044), so a user cannot reach this by choosing
    // anything — it means the request was built with the two settings out of
    // step.
    message: "Label budgeting needs fund budgeting turned on. Nothing was saved.",
  },
  budget_not_draft: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    // The server's own remedy, kept verbatim and with the ids dropped. This is
    // the refusal the port makes VISIBLE: the debounced snapshot targets
    // `initialBudgetDoc.id` even when the budget is not editable, so opening a
    // published budget read-only rewrites it onto itself today. Postgres says
    // no, which is right, and this is the sentence it says it with.
    message:
      "This budget is published and can't be changed. Copy it to a new draft to make changes.",
  },
  budget_cells_shape: {
    sqlstate: "22023",
    retryable: false,
    fault: FAULT_SYSTEM,
    // Passthrough for the same reason the report-parameter family is: it names
    // the expected shape, carries no ids, and is read by a developer. Inventing
    // copy for it would only lose the shape.
    passthrough: true,
  },
  budget_period_month_not_first: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_SYSTEM,
    // Passthrough: "2026-01-15 is not the first of a month" is a date, not an
    // id, and it is the entire diagnosis. A cell is a MONTH; a date that is not
    // the first is a client bug the server refused rather than rounded away.
    passthrough: true,
  },
  budget_label_without_fund: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_SYSTEM,
    passthrough: true,
  },
  budget_account_not_found: {
    sqlstate: "23503",
    retryable: false,
    fault: FAULT_USER,
    message:
      "One of the accounts in this budget is no longer in this organization. Reload the budget and try again.",
  },
  budget_fund_invalid: {
    sqlstate: "23503",
    retryable: false,
    fault: FAULT_SYSTEM,
    // A fund IS its Equity account (schema §1.3). An id that exists but is not
    // Equity passes the plain FK and would silently budget a fund line against
    // an expense head — so this is the app naming the wrong kind of thing, not
    // the user naming a missing one.
    message: "The app budgeted against something that isn't a fund. Nothing was saved.",
  },
  budget_label_not_found: {
    sqlstate: "23503",
    retryable: false,
    fault: FAULT_USER,
    message:
      "One of the labels in this budget is no longer in this organization. Reload the budget and try again.",
  },
  budget_cell_duplicate: {
    sqlstate: "23505",
    retryable: false,
    fault: FAULT_SYSTEM,
    // The grid composes the batch, so two entries for one cell is the app
    // sending itself twice. App copy rather than passthrough because the
    // server's sentence is a tuple of four ids and a date — precise, and
    // meaningless on a screen. The tuple is still on `detail`.
    message: "The app sent the same budget cell twice in one save, so nothing was saved.",
  },
  budget_empty: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    message: "This budget has no amounts in it yet. Enter at least one before publishing.",
  },
  // --- the settings door (0259e) -----------------------------------------
  //
  // Turning a budgeting LEVEL off while that level still holds amounts. Both are
  // refusals rather than cascading deletes, on the house rule that data is
  // corrected to satisfy a constraint and never the reverse — silently removing
  // a customer's typed cells to honour a toggle is the reverse, and the amounts
  // would be gone with nothing on screen to say so.
  //
  // FAULT_USER, and the copy has to be honest about the remedy: there is no door
  // that deletes a cell, so "clear those cells first" would be advice the app
  // cannot take. Starting a fresh draft is the action the user actually has, and
  // naming it is the difference between a refusal and a dead end.
  budget_fund_mode_has_cells: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    message:
      "This budget already has amounts budgeted by fund, so fund budgeting can't be turned off without losing them. Start a new draft without fund budgeting instead. Nothing was changed.",
  },
  budget_label_disable_has_cells: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    message:
      "This budget already has amounts budgeted by label, so label budgeting can't be turned off without losing them. Start a new draft without label budgeting instead. Nothing was changed.",
  },
  // --- publish-time allocation (0064, and the deferred trigger via D4) ----
  //
  // THE ONE PASSTHROUGH PAIR THAT DOES CARRY IDS, against the header's general
  // rule, and deliberately.
  //
  // Everywhere else app copy wins because the server's sentence is a remedy
  // wrapped around opaque ids. Here the ids ARE the answer: "account X, fund Y,
  // for 2026-03: labels sum to 450000c against a fund cell of 400000c" tells
  // the user which cell to open and by how much it is out, and no sentence this
  // app could write reconstructs that. The pre-publish validator (design §3,
  // W4) shows every violation from `budget_over_allocations`; this is what the
  // user sees when one survives to the door anyway.
  //
  // FAULT_USER for both: the person at the screen fixes the cells. Note the
  // asymmetry the two encode — a fund-vs-account over-allocation is REPAIRED by
  // default (`p_reconcile`, and the repair's size comes back as
  // `annual_delta_cents`), so reaching `budget_over_allocated` means either the
  // caller asked not to repair or the repair did not close it; a LABEL-level
  // one is never repaired at any setting, because the labels under a fund are a
  // decision only the user can make.
  budget_over_allocated: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    passthrough: true,
  },
  budget_label_over_allocated: {
    sqlstate: "23514",
    retryable: false,
    fault: FAULT_USER,
    passthrough: true,
  },
};

/**
 * The SQLSTATE each sentinel is documented above as being raised with.
 *
 * Exported because it is the half of the table nothing at runtime consults — the
 * classifier keys on the sentinel alone — so without a test reading it, it could
 * go stale silently. reportErrors.test.js checks every entry against the actual
 * `USING ERRCODE` in supabase/migrations.
 */
export const SENTINEL_SQLSTATES = Object.freeze(
  Object.fromEntries(Object.entries(SENTINELS).map(([token, spec]) => [token, spec.sqlstate])),
);

/**
 * The app copy for each sentinel, exported so nobody writes a second version of
 * it.
 *
 * `src/reports/server/reports/*.js` raise the SAME refusals the ledger functions
 * raise — a shaper that cannot find an account in the chart is telling the user
 * exactly what `account_not_found` tells them — and the first one to do it wrote
 * its own wording ("Account not found"). Two sentences for one condition is two
 * sentences to keep in step, and the ledger's is the one that has been through
 * copy review.
 */
export const SENTINEL_MESSAGES = Object.freeze(
  Object.fromEntries(
    Object.entries(SENTINELS)
      .filter(([, spec]) => !spec.passthrough)
      .map(([token, spec]) => [token, spec.message]),
  ),
);

/**
 * `report_missing_*` is an open FAMILY, unlike everything above.
 * 0014 raises `report_missing_end_date` and `report_missing_start_date`, and a
 * new required parameter would add a third with the same meaning and the same
 * remedy. Matching the family means that one classifies correctly on the day it
 * is written instead of falling into the generic bucket.
 *
 * This is the only prefix rule in the file. Everywhere else exact tokens are
 * required, because elsewhere a prefix match would collapse distinct remedies
 * (see the ledger_unbalanced note).
 */
const REPORT_MISSING = /^report_missing_[a-z_]+$/;

/** The leading `sentinel:` token, anchored so echoed text cannot forge one. */
const SENTINEL_TOKEN = /^([A-Za-z0-9_]+):/;

/** The leading `token:` of a server sentence, or null — the one tokeniser, exported for the write doors. */
export const sentinelToken = (text) => SENTINEL_TOKEN.exec(String(text ?? "").trim())?.[1] ?? null;

/**
 * The SECOND token, for the primitives that spell their refusals
 * `<function>: <token>: <detail>` — 0257's eight (contract §A) and 0214's house
 * style generally.
 *
 * The classifier deliberately does NOT branch on it: one function is one
 * verdict, with one fault, one remedy and one piece of copy, which is why
 * `ledger_entries_page` is a single entry above. But an engineer reading Sentry
 * needs to know WHICH of eight, and `partial_cursor` and `too_many_ids` are
 * completely different bugs in completely different code. So it is lifted out
 * of the detail into an INDEXED tag (`reportIfSystemFault`) rather than left in
 * the unindexed context card where "how often does bad_limit happen?" is
 * unanswerable.
 *
 * Only consulted for PASSTHROUGH verdicts — the family whose `detail` is the
 * server's own sentence. Elsewhere `detail` may carry a memo or an account
 * name, and a second colon in user text must not mint a tag. Even within the
 * family it is naturally narrow: it requires an identifier followed IMMEDIATELY
 * by a colon, so `general_ledger_page: p_limit must be…` and
 * `report_bad_grain: grain must be one of: …` both correctly yield nothing.
 */
const REFUSAL_TOKEN = /^[A-Za-z0-9_]+:\s*([A-Za-z0-9_]+):/;

/**
 * SQLSTATE classes whose failures are DETERMINISTIC — the same request produces
 * the same refusal forever, so "retry" is an infinite loop rather than a remedy.
 *
 *   22  data exception          (bad date, bad numeric literal, bad enum value)
 *   23  integrity constraint    (an FK/unique/check that escaped a named check)
 *   42  syntax error or access rule violation
 *
 * REASONED FROM THE SQLSTATE STANDARD, NOT MEASURED — said plainly so nobody
 * reads it as evidence, in the same spirit as accessGuard's note on PGRST302.
 * It only ever overrides accessGuard's *default* (unknown → retry), never one of
 * its measured verdicts: 42501 is class 42 and still reaches this file as
 * accessGuard's UNAUTHENTICATED, because that verdict is returned before this
 * rule is consulted.
 */
const DETERMINISTIC_CLASSES = {
  22: {
    code: "invalid_request",
    message: "The app sent a request the server couldn't process. Nothing was changed.",
  },
  42: {
    code: "invalid_request",
    message: "The app sent a request the server couldn't process. Nothing was changed.",
  },
  23: {
    code: "constraint_violation",
    // A raw constraint violation reaching a user means the function's named
    // pre-checks missed a case, so it is a system fault even though it looks
    // like something the user did.
    message: "This change conflicts with data already in the books, so nothing was changed.",
  },
};

/** Genuine transaction-rollback failures: the one class where retry IS correct. */
const SERIALIZATION_CODES = new Set(["40001", "40P01"]);

const messageOf = (error) =>
  typeof error?.message === "string" ? error.message : String(error?.message ?? "");

const verdict = (code, spec, { sqlstate, detail, raw }) => ({
  code,
  sqlstate: sqlstate ?? null,
  retryable: spec.retryable,
  fault: spec.fault,
  message: spec.passthrough ? detail : spec.message,
  passthrough: Boolean(spec.passthrough),
  detail,
  raw,
});

/**
 * Map one of accessGuard's verdicts into this module's shape. Deliberately a
 * translation and never a re-decision — the access verdicts were measured, and
 * the only thing added here is the fault/retryable/copy triple.
 */
const ACCESS_VERDICTS = {
  [REVOKED]: {
    code: "not_a_member",
    retryable: false,
    fault: FAULT_USER,
    message: "You no longer have access to this organization.",
  },
  [CONTROL_ENDED]: {
    code: "control_ended",
    retryable: false,
    fault: FAULT_USER,
    message: "Your support session for this organization has ended.",
  },
  [UNAUTHENTICATED]: {
    code: "unauthenticated",
    // The one retryable non-transport verdict: a token refresh is automatic and
    // the same request then works. A MUTATION retried here must carry the same
    // p_request_id — see the header.
    retryable: true,
    fault: FAULT_SYSTEM,
    message: "Your session needs to be refreshed. Try again in a moment.",
  },
  [UNSUPPORTED]: {
    code: "unsupported",
    // NOT retryable, on purpose: PGRST202 means the database does not have the
    // function. Looping cannot conjure it, and a deploy is what fixes it.
    retryable: false,
    fault: FAULT_SYSTEM,
    message: "The server doesn't recognise this request yet — a deploy may still be finishing.",
  },
};

/**
 * The "your token has not loaded yet" verdict, exported whole.
 *
 * `src/reports/server/httpErrors.js` answers a request with NO bearer token at
 * all with exactly this — same code, same copy, same retryable/fault — because
 * it is the same condition arriving one layer earlier. It had been retyped by
 * hand, and a hand-typed copy of user-facing copy is a copy that drifts on the
 * next wording change.
 */
export const UNAUTHENTICATED_VERDICT = Object.freeze(ACCESS_VERDICTS[UNAUTHENTICATED]);

/**
 * The tail verdicts — the three this file mints itself once the sentinel table
 * and accessGuard have both declined to recognise the failure.
 */
const TAIL_VERDICTS = {
  serialization_failure: { retryable: true, fault: FAULT_SYSTEM },
  invalid_request: { retryable: false, fault: FAULT_SYSTEM },
  constraint_violation: { retryable: false, fault: FAULT_SYSTEM },
  transport: { retryable: true, fault: FAULT_SYSTEM },
};

/**
 * EVERY code `classifyRpcError` can return, with the two fields a caller
 * branches on.
 *
 * Exported because the codes were being retyped as string literals by callers
 * that then had to decide something per code — `src/reports/server/httpErrors.js`
 * maps each to an HTTP status — and a literal list is a list that silently stops
 * covering the vocabulary. With this exported, that module's test ITERATES the
 * vocabulary and fails on a code nobody made a decision about, which is how
 * `unsupported` and `constraint_violation` came to be answered 500 by accident
 * rather than on purpose.
 *
 * `report_missing_*` is deliberately absent: it is an open FAMILY (see
 * REPORT_MISSING above), not a code, and it always classifies exactly as
 * `report_bad_grain` does.
 */
export const RPC_VERDICT_SPECS = Object.freeze({
  ...Object.fromEntries(
    Object.entries(SENTINELS).map(([token, spec]) => [
      token,
      { retryable: spec.retryable, fault: spec.fault },
    ]),
  ),
  ...Object.fromEntries(
    Object.values(ACCESS_VERDICTS).map((spec) => [
      spec.code,
      { retryable: spec.retryable, fault: spec.fault },
    ]),
  ),
  ...TAIL_VERDICTS,
});

/** The same vocabulary as a list, for a test that has to enumerate it. */
export const RPC_ERROR_CODES = Object.freeze(Object.keys(RPC_VERDICT_SPECS));

/**
 * Classify one RPC failure. Pure — no state, no side effects, safe on anything
 * including `null`.
 *
 * @param {{code?: string, message?: string} | Error | null} error
 *   The RAW supabase-js error. Pass the object, never an interpolated string:
 *   flattening `code` into text is what src/data/useOrgRefdata.js's `rpcFailure`
 *   comment exists to prevent, and it defeats both this classifier and the
 *   access guard.
 * @param {{basis?: string}} [ctx] Forwarded to accessGuard so a session holding
 *   an org by an admin control session gets CONTROL_ENDED rather than REVOKED.
 * @returns {null | {
 *   code: string, sqlstate: string|null, retryable: boolean,
 *   fault: "user"|"system", message: string, passthrough: boolean,
 *   detail: string, raw: unknown
 * }}  `null` when there is no error — so the caller's branch is `if (error)`
 *   rather than a verdict object that is always truthy.
 */
export function classifyRpcError(error, { basis } = {}) {
  if (!error) return null;

  const sqlstate = error.code ?? null;
  const detail = messageOf(error);
  const ctx = { sqlstate, detail, raw: error };

  const token = SENTINEL_TOKEN.exec(detail)?.[1];
  if (token) {
    const spec = SENTINELS[token];
    if (spec) return verdict(token, spec, ctx);
    if (REPORT_MISSING.test(token)) {
      return verdict(token, { retryable: false, fault: FAULT_SYSTEM, passthrough: true }, ctx);
    }
  }

  // No N3 sentinel. Everything below was already measured in accessGuard; this
  // asks it rather than re-deriving it.
  const access = classifyAccessError(error, { basis });
  const mapped = ACCESS_VERDICTS[access.verdict];
  if (mapped) return verdict(mapped.code, mapped, ctx);

  // accessGuard said TRANSIENT, which is its safe default for anything it does
  // not recognise. For a session guard "retry" is the right default; for a write
  // it is not always, so two narrower readings apply first.
  if (access.verdict === TRANSIENT && SERIALIZATION_CODES.has(sqlstate)) {
    return verdict(
      "serialization_failure",
      {
        ...TAIL_VERDICTS.serialization_failure,
        message: "The books were busy for a moment. Try that again.",
      },
      ctx,
    );
  }

  const deterministic =
    typeof sqlstate === "string" && DETERMINISTIC_CLASSES[sqlstate.slice(0, 2)];
  if (deterministic) {
    return verdict(
      deterministic.code,
      { ...TAIL_VERDICTS[deterministic.code], message: deterministic.message },
      ctx,
    );
  }

  // A dropped socket, a 5xx, a statement timeout, or something nobody has named.
  // Retryable, because the safe default for an unknown transport failure is to
  // try again — and because a mutation carrying p_request_id makes that safe.
  return verdict(
    "transport",
    {
      ...TAIL_VERDICTS.transport,
      message: "Couldn't reach the server. Check your connection and try again.",
    },
    ctx,
  );
}

/**
 * The reporting half of the FAULT partition the header describes.
 *
 * FAULT_USER is a screen, not an incident: the person can balance the entry,
 * unclear the reconciliation, or ask an admin for a role. Nobody is paged and
 * nothing is emitted.
 *
 * FAULT_SYSTEM is split once more, by the verdict's OWN `retryable` field,
 * because "worth reporting to Sentry" and "system fault" are not the same set:
 *
 *   NOT retryable  — `unsupported` (the database does not have the function we
 *     just called: deploy skew), `ledger_request_id_conflict` (a client defect —
 *     the user cannot see or choose a request id), `invalid_request`,
 *     `constraint_violation` (a named pre-check missed a case), `report_bad_*`
 *     and `report_missing_*` (the app built the request wrong). Every one of
 *     these is a defect that stays broken until someone ships something.
 *     ONE EVENT EACH.
 *
 *   retryable — `transport` (a dropped socket), `unauthenticated` (a token that
 *     has not loaded yet), `serialization_failure` (ordinary contention). These
 *     are operational weather. A user on hotel wifi would mint one Sentry issue
 *     per RPC, which is the scattered-noise anti-pattern wearing a Sentry badge:
 *     it buys nothing and drowns the eight verdicts above. BREADCRUMB ONLY —
 *     so when something that IS reportable happens next, the churn that preceded
 *     it rides along on that event.
 *
 * Called from `createRpc.rpc` so every door written against this seam is
 * instrumented on the day it is written, and no call site has to remember.
 *
 * @param {ReturnType<typeof classifyRpcError>} verdict
 * @param {{orgId?: string|null, requestId?: string|null, fn?: string,
 *          durationMs?: number, extra?: Record<string, unknown>}} [ctx]
 *   `requestId` is the whole point of the FAULT_SYSTEM half being reportable at
 *   all: it is the `p_request_id` the server persisted on `entry_versions`, so
 *   an engineer holding this event can answer "did the write commit?" with
 *   `SELECT * FROM entry_versions WHERE request_id = …` instead of guessing.
 *
 *   `extra` is unindexed Sentry CONTEXT, never tags — telemetry.js's split, for
 *   its reason: tags are a bounded, low-cardinality vocabulary and context is
 *   where the specifics go. It is merged UNDER the fields computed here, so a
 *   caller cannot overwrite `detail` or `duration_ms` with something that reads
 *   like them and is not.
 * @returns {boolean} whether an event (not a breadcrumb) was captured.
 */
export function reportIfSystemFault(verdict, ctx = {}) {
  if (verdict?.fault !== FAULT_SYSTEM) return false;

  const { orgId = null, requestId = null, fn = null, durationMs = null, extra } = ctx;
  const tags = {
    code: verdict.code,
    sqlstate: verdict.sqlstate,
    rpc: fn,
    request_id: requestId,
    retryable: verdict.retryable,
    // Which of the function's refusals, for the primitives whose verdict is
    // their own name (see REFUSAL_TOKEN). Null everywhere else, and a null tag
    // is dropped rather than written — createCapture skips null values.
    refusal_token: verdict.passthrough
      ? (REFUSAL_TOKEN.exec(verdict.detail ?? "")?.[1] ?? null)
      : null,
  };
  // The classifier's own fields are spread LAST, so a caller cannot overwrite
  // `detail` or `passthrough` with something of its own and quietly change what
  // the event says the server did.
  const context = {
    ...(extra ?? {}),
    detail: verdict.detail,
    passthrough: verdict.passthrough,
    duration_ms: durationMs,
  };

  if (verdict.retryable) {
    dataBreadcrumb("data.rpc", `${fn ?? "rpc"} failed: ${verdict.code}`, {
      ...tags,
      ...context,
      org_id: orgId,
    });
    return false;
  }

  // The raw PostgrestError when there is one — it is an Error subclass in
  // supabase-js v2, so it carries a stack and groups by where it came from.
  // The synthesized message is the fallback, and is constant per code so the
  // fallback groups too.
  const fault =
    verdict.raw instanceof Error ? verdict.raw : `RPC refused: ${verdict.code}`;

  captureDataFault(fault, {
    component: "rpc",
    action: fn ?? "rpc",
    orgId,
    level: "error",
    tags,
    context,
  });
  return true;
}

export default classifyRpcError;
